import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Package, Truck, ClipboardList, TrendingUp, FileText, Wallet, Crown, User, LogOut, Bell, Search, Menu, X, ChevronDown, MessageCircle, Map } from 'lucide-react';
import { currentUser as mockUser, notifications } from '../../mock/mockData';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { getUser, logout as doLogout, subscriptionApi } from '../../lib/api';
import { useI18n } from '../../lib/i18n';
import LanguageToggle from '../LanguageToggle';
import WhatsAppPanel from '../WhatsAppPanel';

const navItemsOld = [
  { to: '/app/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/app/loads', icon: Package, label: 'Load Board' },
  { to: '/app/trucks', icon: Truck, label: 'Truck Board' },
  { to: '/app/my-loads', icon: ClipboardList, label: 'My Loads' },
  { to: '/app/insights', icon: TrendingUp, label: 'Rate Insights' },
  { to: '/app/documents', icon: FileText, label: 'Documents' },
  { to: '/app/payments', icon: Wallet, label: 'Payments' },
  { to: '/app/subscription', icon: Crown, label: 'Subscription' },
];

const navItemsDefs = [
  { to: '/app/dashboard', icon: LayoutDashboard, key: 'dashboard' },
  { to: '/app/loads', icon: Package, key: 'loadBoard' },
  { to: '/app/trucks', icon: Truck, key: 'truckBoard' },
  { to: '/app/fleet-map', icon: Map, key: 'fleetMap' },
  { to: '/app/my-loads', icon: ClipboardList, key: 'myLoads' },
  { to: '/app/insights', icon: TrendingUp, key: 'rateInsights' },
  { to: '/app/documents', icon: FileText, key: 'documents' },
  { to: '/app/payments', icon: Wallet, key: 'payments' },
  { to: '/app/subscription', icon: Crown, key: 'subscription' },
];

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [sub, setSub] = useState({ plan: 'Free Trial', days_left: 30 });
  const navigate = useNavigate();
  const location = useLocation();
  const unread = notifications.filter(n => n.unread).length;
  const { t } = useI18n();

  const stored = getUser();
  const currentUser = stored ? {
    ...mockUser,
    name: stored.name,
    company: stored.company,
    role: stored.role === 'fleet' ? 'Fleet Owner' : stored.role === 'shipper' ? 'Shipper' : stored.role === 'broker' ? 'Broker' : 'Driver',
    phone: stored.phone,
    city: stored.city,
    avatar: (stored.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase(),
  } : mockUser;

  React.useEffect(() => {
    if (localStorage.getItem('ezload_token')) {
      subscriptionApi.get().then(setSub).catch(() => {});
    }
  }, []);

  const handleLogout = () => { doLogout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-[#F7FAF9] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-[#0B2545] text-white z-40 transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg brand-gradient flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-lg tracking-tight font-[Poppins]">EZLOAD</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="px-3 py-4 space-y-1">
          {navItemsDefs.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-[#00A651] text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
            >
              <item.icon className="w-[18px] h-[18px]" />
              {t(item.key)}
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/10">
          <div className="bg-white/5 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-4 h-4 text-[#FFB800]" />
              <span className="text-xs font-semibold">{sub.plan === 'Free Trial' ? t('freeTrial') : sub.plan}</span>
            </div>
            <p className="text-xs text-white/60 mb-2">{sub.days_left} {t('daysLeft')}</p>
            <Button onClick={() => navigate('/app/subscription')} size="sm" className="w-full bg-[#00A651] hover:bg-[#008a43] text-white text-xs h-8">{t('upgradeNow')}</Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} className="fixed inset-0 bg-black/50 z-30 lg:hidden" />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center px-4 lg:px-6 sticky top-0 z-20">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden mr-3">
            <Menu className="w-6 h-6 text-[#0B2545]" />
          </button>

          <div className="hidden md:flex items-center flex-1 max-w-md">
            <div className="relative w-full">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input placeholder="Search loads, trucks, cities..." className="pl-9 bg-gray-50 border-gray-200 h-10" />
            </div>
          </div>

          <div className="ml-auto flex items-center gap-1 sm:gap-2">
            <LanguageToggle compact />
            <button onClick={() => setWaOpen(true)} className="relative p-2 rounded-lg hover:bg-gray-100" title="WhatsApp Assistant">
              <MessageCircle className="w-5 h-5 text-[#075E54]" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-[#00A651] rounded-full"></span>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative p-2 rounded-lg hover:bg-gray-100">
                  <Bell className="w-5 h-5 text-[#0B2545]" />
                  {unread > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Notifications ({unread} unread)</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifications.map(n => (
                  <DropdownMenuItem key={n.id} className="flex flex-col items-start py-3">
                    <div className="flex items-start justify-between w-full">
                      <span className="font-medium text-sm">{n.title}</span>
                      {n.unread && <span className="w-2 h-2 bg-[#00A651] rounded-full mt-1.5"></span>}
                    </div>
                    <span className="text-xs text-gray-500">{n.desc}</span>
                    <span className="text-xs text-gray-400 mt-0.5">{n.time}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg hover:bg-gray-100">
                  <div className="w-8 h-8 rounded-full brand-gradient flex items-center justify-center text-white text-xs font-bold">{currentUser.avatar}</div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-semibold text-[#0B2545] leading-tight">{currentUser.name}</p>
                    <p className="text-xs text-gray-500 leading-tight">{currentUser.role}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{currentUser.company}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/app/profile')}><User className="w-4 h-4 mr-2" />Profile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/app/subscription')}><Crown className="w-4 h-4 mr-2" />Subscription</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600"><LogOut className="w-4 h-4 mr-2" />Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      <WhatsAppPanel open={waOpen} onClose={() => setWaOpen(false)} />
    </div>
  );
}
